import { Stack, StyleProps, Text, VStack } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import Chart from 'chart.js/auto';

import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";
import { ConsumptionsStructure } from "@bcasmart/doxecure-commons/doxecureTypes";

import { G1Button } from "@components/G1Button";
import { SeaShellBox } from "@components/SeaShellBox";
import { localtrans } from "@utils/LocalTranslations";

export interface ConsumptionsBoxProperties extends StyleProps {
    currentMonth: {
        nbFrees: number;
        nbPaid: number;
        nbRefused?: number;
        inProgress?: number;
    }
    allConsumptions: Nullable<ConsumptionsStructure>;
    onExport: () => void;
}

export const ConsumptionsBox = (props: ConsumptionsBoxProperties) => {
    const {currentMonth, allConsumptions, mt, bg, onExport} = props;

    const currentPaidConsumptions = currentMonth.nbPaid + (currentMonth.inProgress ?? 0) + (currentMonth.nbRefused ?? 0);
    let paidInfo = "";
    if ($ok(currentMonth.nbRefused) && currentMonth.nbRefused! > 0) {
        paidInfo = `dont ${currentMonth.nbRefused!} signature${currentMonth.nbRefused! > 1 ? 's' : ''} refusée${currentMonth.nbRefused! > 1 ? 's' : ''}`;
    }
    if ($ok(currentMonth.inProgress) && currentMonth.inProgress! > 0) {
        paidInfo = `${paidInfo.length === 0 ? `dont ` : `${paidInfo} et `} ${currentMonth.inProgress!}${paidInfo.length === 0 ? ` signature${currentMonth.inProgress! > 1 ? 's' : ''}` : ""} en cours de traitement`;
    }

    return  (
        <SeaShellBox component="form" spacing="5" bg={bg} mt={mt}>
            <Stack width="100%">
                <Text width="100%" fontWeight="bold">{localtrans('currentMonthConsumptions')}:</Text>
                <Text fontSize="medium">{localtrans('currentFreeConsumptions')}: {currentMonth.nbFrees}</Text>
                <Text as="div" fontSize="medium">{localtrans('currentPaidConsumptions')} : {currentPaidConsumptions}
                    {paidInfo.length > 0 && <Text fontSize="small" fontStyle="italic">{paidInfo}</Text>}
                </Text>
            </Stack>

            <VStack width="100%">
                <Text width="100%" fontWeight="bold">Consommation des 12 derniers mois</Text>
                <ColumnChart data={allConsumptions} />
                <G1Button component="global" variant="normal" title="Exporter" marginRight="2%" fontSize="md" onClick={ onExport } />
            </VStack>
        </SeaShellBox>
    );
}

interface ColumnChartProperties {
    data: Nullable<ConsumptionsStructure>;
}

const ColumnChart = ({ data }: ColumnChartProperties) => {
    const chartRef = useRef<HTMLCanvasElement|null>(null);
    const [chart, setChart] = useState<Chart|null>(null);
  
    useEffect(() => {
        if ($ok(data) && chartRef && chartRef.current) {
            if ($ok(chart)) { chart!.destroy(); }
            const labels: string[] = [];
            const frees: number[] = [];
            const paidCompleted: number[] = [];
            const inprogress: number[] = [];
            for (const key in data) {
                labels.push(key);
                frees.push(data[key].signed.qualified);

                const totalPaid = data[key].signed.emailOTP + (data[key].signed.smsOTP ?? 0) + (data[key].refused ?? 0);
                const paidInProgress = data[key].inprogress ?? 0;
                paidCompleted.push(totalPaid);
                inprogress.push(paidInProgress);
            }

            const ctx = chartRef.current.getContext('2d');
            setChart(new Chart(ctx!, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [{
                        label: localtrans('FreeConsumptions'),
                        data: frees,
                        backgroundColor: 'rgba(54, 162, 100, 0.2)',
                        borderColor: 'rgba(54, 162, 100, 1)',
                        borderWidth: 1
                    },
                    {
                        label: localtrans('PaidConsumptions'),
                        data: paidCompleted,
                        backgroundColor: 'rgba(54, 162, 235, 0.2)',
                        borderColor: 'rgba(54, 162, 235, 1)',
                        borderWidth: 1,
                        stack: 'paidAndProgress'
                    },
                    {
                        label: localtrans('InProgressConsumptions'),
                        data: inprogress,
                        backgroundColor: 'rgba(235, 162, 54, 0.2)',
                        borderColor: 'rgba(235, 162, 54, 1)',
                        borderWidth: 1,
                        stack: 'paidAndProgress'
                    }]
                },
                options: {
                    responsive: true,
                    scales: {
                        x: { stacked: true },
                        y: { beginAtZero: true, stacked: true }
                    },
                    plugins: {
                        tooltip: {
                            callbacks: {
                                label: (context) => {
                                    if (context.dataset.label === localtrans('InProgressConsumptions')) {
                                        const total = paidCompleted[context.dataIndex] + inprogress[context.dataIndex];
                                        return `${context.dataset.label}: ${context.parsed.y} (sur ${total} payantes)`;                                        
                                    }
                                    return `${context.dataset.label}: ${context.parsed.y}`;
                                }
                            }
                        }
                    }
                }
            }));
      }
    }, [data]);

    return <canvas ref={chartRef} />;
};